import { useEffect, useState } from 'react';
import {
  dialerDispositionLabels,
  dialerDispositions,
  formatCallDuration,
  formatPhoneDisplay,
  formatTime,
  toBerlinDateKey,
  type DialerDisposition,
} from '@brimo/shared';
import type { CallSummary } from '../../telephony/api';
import { noteWithSummary } from '../../telephony/useCallSummary';
import { clsx } from '../../../ds/clsx';
import { Banner, TextField } from '../../../ds/components';
import type { NextUp } from '../api';
import { Eyebrow, FactRow } from '../ui';
import styles from './session.module.css';

export interface DispositionDraft {
  disposition: DialerDisposition | null;
  note: string;
  followUpOn: string;
  followUpTime: string;
}

/** What follows from a result (the server's rules of api-dialer.md „Dispositions"). */
function consequence(disposition: DialerDisposition, name: string): string {
  switch (disposition) {
    case 'appointment_set':
      return 'Lifecycle → Call gebucht · Wiedervorlage entfällt';
    case 'follow_up':
      return `Lifecycle → Follow Up · Aufgabe „Wiedervorlage: ${name}“`;
    case 'not_reached':
      return `Aufgabe „Erneut anrufen: ${name}“ am nächsten Werktag, 9:00`;
    case 'no_interest':
      return 'Lifecycle → Kein Interesse · offene Anrufe entfallen';
    case 'wrong_number':
      return `Aufgabe „Nummer prüfen: ${name}“`;
    case 'block':
      return 'DNC · offene Aufgaben werden geschlossen';
  }
}

/**
 * Terminierung 10 — Gesprächsende · Ergebnis: the six results (one chosen; „Termin vereinbart" preselected
 * after a booking), what follows, the note of the call, the follow-up date for „Wiedervorlage",
 * „Speichern und weiter wählen" / „Queue pausieren"; right the navy „Als nächstes" with the power
 * countdown after saving (visible and cancellable).
 */
export function DispositionView({
  name,
  durationSeconds,
  recordingSaved,
  booked,
  draft,
  onDraft,
  errors,
  banner,
  saving,
  saved,
  countdown,
  dialMode,
  nextUp,
  suggestion,
  aiHint = null,
  onSave,
  onPause,
  onCancelCountdown,
  onNext,
}: {
  name: string;
  durationSeconds: number | null;
  recordingSaved: boolean;
  booked: boolean;
  draft: DispositionDraft;
  onDraft: (draft: DispositionDraft) => void;
  errors: { reason?: string | undefined; followUp?: string | undefined };
  banner: string | null;
  saving: boolean;
  /** The result is stored; the page waits for the countdown or „Weiter wählen". */
  saved: boolean;
  countdown: number | null;
  dialMode: 'power' | 'preview';
  nextUp: NextUp | null;
  suggestion: string | null;
  /** „KI-Zusammenfassung wird erstellt…" while the summary loads, the „Vorschlag" line once it prefilled the result. */
  aiHint?: string | null;
  onSave: () => void;
  onPause: () => void;
  onCancelCountdown: () => void;
  onNext: () => void;
}) {
  const header = [
    durationSeconds === null ? 'Nicht erreicht' : `Gespräch beendet · ${formatCallDuration(durationSeconds)}`,
    recordingSaved ? 'Aufnahme gespeichert' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const chosen = draft.disposition;
  const needsReason = chosen === 'no_interest' || chosen === 'block';
  const [editingNote, setEditingNote] = useState(false);
  const showNoteText = draft.note.trim() !== '' && !editingNote && !errors.reason;

  return (
    <div className={styles.twoColumns}>
      <section className={styles.dispositionCard} aria-labelledby="dialer-disposition-title">
        <div className={styles.dispositionHead}>
          <Eyebrow tone="accent">{saved ? 'Ergebnis gespeichert' : 'Ergebnis erforderlich'}</Eyebrow>
          <span className={styles.dispositionMeta}>{header}</span>
        </div>
        <h2 id="dialer-disposition-title" className={styles.h2}>
          {name}
        </h2>
        {banner ? (
          <div className={styles.blockGap}>
            <Banner tone="danger">{banner}</Banner>
          </div>
        ) : null}
        {suggestion ? (
          <div className={styles.blockGap}>
            <Banner tone="warning">{suggestion}</Banner>
          </div>
        ) : null}
        {aiHint && !saved ? (
          <p className={styles.aiHint} aria-live="polite">
            {aiHint}
          </p>
        ) : null}
        <div className={styles.chips} role="radiogroup" aria-label="Ergebnis">
          {dialerDispositions.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={chosen === value}
              disabled={saved || saving || (value === 'appointment_set' && !booked)}
              className={clsx(styles.chip, chosen === value && styles.chipOn)}
              onClick={() => onDraft({ ...draft, disposition: value })}
            >
              {dialerDispositionLabels[value]}
            </button>
          ))}
        </div>
        <div className={styles.followBox}>
          {chosen !== null ? (
            <div className={styles.followRow}>
              <span className={styles.followLabel}>
                {chosen === 'appointment_set' ? 'Folgt aus der Buchung' : 'Folgt daraus'}
              </span>
              <span className={styles.followValue}>{consequence(chosen, name)}</span>
            </div>
          ) : null}
          {chosen === 'follow_up' ? (
            <div className={clsx(styles.followRow, styles.followSplit, styles.followFields)}>
              <TextField
                label="Wiedervorlage am"
                type="date"
                size="compact"
                required
                disabled={saved}
                value={draft.followUpOn}
                error={errors.followUp}
                onChange={(event) => onDraft({ ...draft, followUpOn: event.target.value })}
              />
              <TextField
                label="Uhrzeit"
                type="time"
                size="compact"
                optional
                disabled={saved}
                value={draft.followUpTime}
                onChange={(event) => onDraft({ ...draft, followUpTime: event.target.value })}
              />
            </div>
          ) : null}
          <div className={clsx(styles.followRow, chosen !== null && styles.followSplit)}>
            <label className={styles.followLabel} htmlFor="dialer-disposition-note">
              {needsReason ? 'Notiz aus dem Gespräch · Grund' : 'Notiz aus dem Gespräch'}
            </label>
            {showNoteText ? (
              <span className={styles.noteText}>
                {draft.note}
                {!saved ? (
                  <>
                    {' '}
                    <button type="button" className={styles.linkButton} onClick={() => setEditingNote(true)}>
                      ändern
                    </button>
                  </>
                ) : null}
              </span>
            ) : (
              <textarea
                id="dialer-disposition-note"
                className={clsx(styles.noteInline, errors.reason && styles.noteInvalid)}
                rows={2}
                disabled={saved}
                value={draft.note}
                aria-invalid={errors.reason ? true : undefined}
                aria-describedby={errors.reason ? 'dialer-disposition-reason' : undefined}
                onChange={(event) => onDraft({ ...draft, note: event.target.value })}
              />
            )}
          </div>
          {errors.reason ? (
            <p id="dialer-disposition-reason" className={styles.fieldError}>
              {errors.reason}
            </p>
          ) : null}
        </div>
        <div className={styles.ctaRow}>
          {saved ? (
            <button type="button" className={styles.cta} disabled={countdown !== null} onClick={onNext}>
              Weiter wählen
            </button>
          ) : (
            <button
              type="button"
              className={styles.cta}
              disabled={chosen === null || saving}
              aria-busy={saving || undefined}
              onClick={onSave}
            >
              Speichern und weiter wählen
            </button>
          )}
          <button type="button" className={styles.ctaSecondary} disabled={saving} onClick={onPause}>
            Queue pausieren
          </button>
          {!saved ? (
            <span className={styles.ctaHint}>Ohne Ergebnis wählt die Queue nicht weiter.</span>
          ) : null}
        </div>
      </section>
      <section className={styles.navyCard} aria-label="Als nächstes">
        <Eyebrow tone="inverse">Als nächstes</Eyebrow>
        {nextUp ? (
          <>
            <div className={styles.navyName}>{nextUp.name}</div>
            {nextUp.phone ? <div className={styles.navyPhone}>{formatPhoneDisplay(nextUp.phone)}</div> : null}
            <div className={styles.navyFacts}>
              <FactRow inverse label="Liste" value={nextUp.listName ?? '—'} />
              <FactRow inverse label="Status" value={nextUp.lifecycle.label} />
              <FactRow inverse label="Versuch" value={nextUp.attempt} />
            </div>
          </>
        ) : (
          <div className={styles.navyEmpty}>Gerade kein weiterer Kontakt wählbar.</div>
        )}
        <div className={styles.navyNote} aria-live="polite">
          {countdown !== null ? (
            <span className={styles.countdown}>
              Wählt automatisch in {countdown} {countdown === 1 ? 'Sekunde' : 'Sekunden'}.
              <button type="button" className={styles.countdownCancel} onClick={onCancelCountdown}>
                Abbrechen
              </button>
            </span>
          ) : dialMode === 'power' && !saved ? (
            'Wählt automatisch in 5 Sekunden, sobald das Ergebnis gespeichert ist.'
          ) : dialMode === 'preview' ? (
            'Im Preview-Modus starten Sie den nächsten Anruf selbst.'
          ) : (
            'Automatisches Wählen angehalten.'
          )}
        </div>
      </section>
    </div>
  );
}

/** Defaults of a new result: „Termin vereinbart" after a booking, „Nicht erreicht" without a connection. */
export function useDispositionDefault(
  key: string | null,
  booked: boolean,
  reached: boolean,
  draft: DispositionDraft,
  onDraft: (draft: DispositionDraft) => void,
): void {
  const [applied, setApplied] = useState<string | null>(null);
  useEffect(() => {
    if (key === null || applied === key) return;
    setApplied(key);
    if (draft.disposition !== null) return;
    if (booked) onDraft({ ...draft, disposition: 'appointment_set' });
    else if (!reached) onDraft({ ...draft, disposition: 'not_reached' });
  }, [key, applied, booked, reached, draft, onDraft]);
}

/** The dialer chip for a suggested disposition; „Termin vereinbart" only after a booking in this call. */
function dialerChipOf(value: CallSummary['suggestedDisposition'], booked: boolean): DialerDisposition | null {
  switch (value) {
    case 'appointment_set':
      return booked ? 'appointment_set' : null;
    case 'follow_up':
    case 'callback_requested':
      return 'follow_up';
    case 'not_reached':
    case 'voicemail':
      return 'not_reached';
    case 'no_interest':
    case 'wrong_number':
    case 'block':
      return value;
    case 'documents_promised':
    case null:
      return null;
  }
}

/**
 * Prefills the result from the AI summary: the note (below the setter's own lines), the suggested result when none is
 * chosen yet, and for „Wiedervorlage" the suggested date and time when none is entered. Everything stays editable.
 */
export function applySummary(
  draft: DispositionDraft,
  summary: CallSummary,
  booked: boolean,
): DispositionDraft {
  const chip = draft.disposition ?? dialerChipOf(summary.suggestedDisposition, booked);
  const next: DispositionDraft = {
    ...draft,
    note: noteWithSummary(draft.note, summary.summary),
    disposition: chip,
  };
  if (chip === 'follow_up' && summary.suggestedFollowUpAt !== null && draft.followUpOn === '') {
    next.followUpOn = toBerlinDateKey(summary.suggestedFollowUpAt);
    next.followUpTime = formatTime(summary.suggestedFollowUpAt);
  }
  return next;
}
